<template>
  <h2>{{ t('summary') }}</h2>
  <div v-if="isLoading" class="flex h-[172px] items-center justify-center">
    <Spinner></Spinner>
  </div>
  <div v-if="!isLoading" class="mt-4 grid gap-4 md:grid-cols-3">
    <LabeledText :label="t('milesDriven')">
      {{ enabled ? totalMilage + ' km' : '-' }}
    </LabeledText>

    <LabeledText :label="t('milesIncluded')">
      {{ totalInclusiveKmString }}
    </LabeledText>

    <LabeledText
      :label="`${t('extraKm')} (${formatCurrency(booking.priceCalculation.pricePerExtraKm ?? 0)})`"
    >
      {{ extraKmString }}
      -
      <span v-currency="additionalCosts?.costsOfExtraKm ?? 0" />
    </LabeledText>

    <LabeledText :label="`${t('missingFuel')}`">
      {{ formatFloat(additionalCosts?.missingFuel) }}
    </LabeledText>

    <LabeledText :label="t('missingBattery')">
      {{ formatFloat(additionalCosts?.missingBattery) }}
    </LabeledText>

    <LabeledText :label="`${t('fuelCosts')}`">
      <span v-currency="costsOfRefueling" />
    </LabeledText>

    <LabeledText v-if="!isLongTerm" :label="`${t('extraDuration')}`">
      <span>{{ extraDuration }} {{ t('days', extraDuration) }}</span>
    </LabeledText>

    <LabeledText v-if="!isLongTerm" :label="`${t('costsOfExtraDuration')}`">
      <span v-currency="additionalCosts?.costsOfExtraDuration" />
    </LabeledText>

    <LabeledText :label="t('totalExtraCosts')" class="align-right justify-self-end md:col-start-3">
      <p
        v-currency="additionalCosts?.totalAdditionalCosts"
        class="text-base font-bold lg:text-lg"
      />
    </LabeledText>
  </div>
</template>

<script setup lang="ts">
import LabeledText from '@/components/LabeledText.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { useRequestAdditionalCosts } from '@/queries/use-pricing';
import { formatCurrency, formatFloat } from '@/utils/format-numbers';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { sum } from 'lodash';
import { isLongTermPricing } from '@/utils/pricing.utils';

const { t } = useI18n();
const route = useRoute();
const props = defineProps<{
  booking: Booking;
  returnDate?: string;
}>();

const enabled = computed(() =>
  props.booking.allocations.every(
    (a) => typeof a.milageEnd === 'number' && typeof a.fuelLevelEnd === 'number',
  ),
);
const { data: additionalCosts, isLoading } = useRequestAdditionalCosts(
  computed(() => ({
    bookingId: route.params.id as string,
    returnDate: props.returnDate ? new Date(props.returnDate) : undefined,
  })),
  enabled,
);

const costsOfRefueling = computed(() => {
  return (
    (additionalCosts.value?.costsOfRefueling ?? 0) + (additionalCosts.value?.costsOfRecharging ?? 0)
  );
});

const totalMilage = computed(() =>
  sum(
    props.booking.allocations.map((a) =>
      a.milageEnd !== null && a.milageStart !== null ? a.milageEnd - a.milageStart : null,
    ),
  ),
);

const totalInclusiveKmString = computed(() => {
  if (!enabled.value) {
    return '-';
  }

  const includedKms = additionalCosts.value?.includedKms ?? 0;
  const additionalBookedKm = additionalCosts.value?.additionalBookedKmExtraValue ?? 0;

  return `${includedKms} km + ${additionalBookedKm} ${t('additionalBookedKm')}`;
});
const extraKmString = computed(() => {
  return enabled.value ? (additionalCosts.value?.extraKm ?? 0) + ' km' : 'n.a.';
});

const extraDuration = computed(() => Math.max(additionalCosts.value?.extraDuration ?? 0, 0));
const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));
</script>

<i18n lang="json">
{
  "en": {
    "milesDriven": "Miles Driven",
    "enterMilage": "Please enter the meter milage",
    "summary": "Summary",
    "milesIncluded": "Miles Included",
    "extraKm": "Kilometers Extra",
    "missingFuel": "Missing Fuel",
    "missingBattery": "Missing Battery",
    "fuelCosts": "Fuel Costs",
    "totalExtraCosts": "Total Extra Costs",
    "additionalBookedKm": "Extra km",
    "couponKm": "Coupon km",
    "days": "days|day|days",
    "extraDuration": "Extra duration",
    "costsOfExtraDuration": "Costs of extra duration"
  },
  "de": {
    "milesDriven": "Gefahrene Kilometer",
    "enterMilage": "Bitte geben Sie den Kilometerstand an",
    "summary": "Zusammenfassung",
    "milesIncluded": "Inklusive Kilometer",
    "extraKm": "Zusatzkilometer",
    "missingFuel": "Fehlender Kraftstoff",
    "missingBattery": "Fehlende Batterieladung",
    "fuelCosts": "Tank- / Ladekosten",
    "totalExtraCosts": "Zusatzkosten",
    "additionalBookedKm": "Extra-KM",
    "couponKm": "Gutschein-KM",
    "days": "Tage|Tag|Tage",
    "extraDuration": "Zusätzliche Dauer",
    "costsOfExtraDuration": "Kosten für zusätzliche Dauer"
  },
  "es": {
    "milesDriven": "Kilómetros recorridos",
    "enterMilage": "Introduzca el kilometraje del cuentakilómetros",
    "summary": "Resumen",
    "milesIncluded": "Kilómetros incluidos",
    "extraKm": "Kilómetros adicionales",
    "missingFuel": "Combustible faltante",
    "missingBattery": "Carga de batería faltante",
    "fuelCosts": "Coste de combustible / carga",
    "totalExtraCosts": "Costes adicionales totales",
    "additionalBookedKm": "Kilómetros extra contratados",
    "couponKm": "Km de cupón",
    "days": "días|día|días",
    "extraDuration": "Duración adicional",
    "costsOfExtraDuration": "Coste por duración adicional"
  }
}
</i18n>
